import React, { lazy, Suspense, useEffect } from 'react';
import moment from 'moment';
import { Router, Switch, Route } from 'react-router-dom';
import { createBrowserHistory, History } from 'history';
import { QueryClientProvider, QueryClient } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ErrorBoundary } from 'react-error-boundary';

import '@/style/App.scss';
import 'moment/locale/ar-ly';
import 'moment/locale/es-us';
import 'moment/locale/fr';
import 'moment/locale/sv';

import AppIntlLoader from './AppIntlLoader';
import { useAppIntlContext } from './AppIntlProvider';
import { EnsureAuthenticated } from '@/components/Guards/EnsureAuthenticated';
import GlobalErrors from '@/containers/GlobalErrors/GlobalErrors';

import { SplashScreen, DashboardThemeProvider } from '../components';
import { DialogProvider } from '@/hooks/useDialog';
import { queryConfig } from '../hooks/query/base';
import { EnsureUserEmailNotVerified } from './Guards/EnsureUserEmailNotVerified';
import BrandedLoading from './Dashboard/BrandedLoading';
import DashboardErrorBoundary from './Dashboard/DashboardErrorBoundary';

// Locale moment PAR DEFAUT, posee des le chargement du module — les `import`
// etant hisses, ce defaut s'applique avant tout rendu.
// `AppIntlLoader` ne repositionne `moment.locale()` qu'une fois le bundle i18n
// resolu (import dynamique) : sans ce defaut, tout ce qui est rendu avant —
// ecran de connexion, splash, premier paint d'un ecran en cache — sortait en
// anglais (« 08 Dec 2025 »). Le produit est francais : le defaut doit l'etre.
moment.locale('fr');

const DashboardPrivatePages = lazy(
  () => import('@/components/Dashboard/PrivatePages'),
);
const AuthenticationPage = lazy(
  () => import('@/containers/Authentication/AuthenticationPage'),
);
const EmailConfirmation = lazy(
  () => import('@/containers/Authentication/EmailConfirmation'),
);
const RegisterVerify = lazy(
  () => import('@/containers/Authentication/RegisterVerify'),
);
const OneClickDemoPage = lazy(
  () => import('@/containers/OneClickDemo/OneClickDemoPage'),
);
const PaymentPortalPage = lazy(
  () => import('@/containers/PaymentPortal/PaymentPortalPage'),
);
const MissingDocsPortalPage = lazy(
  () => import('@/containers/MissingDocs/PortalPage'),
);
const GEDPortalPage = lazy(
  () => import('@/containers/GEDPortal/GEDPortalPage'),
);
const NotFoundPage = lazy(() => import('@/containers/NotFound/NotFoundPage'));

/**
 * Synchronise `<html lang>` / `<html dir>` avec la langue reellement chargee.
 *
 * `public/index.html` sert une valeur de demarrage, mais elle doit suivre la
 * langue de l'utilisateur au runtime (F72) : l'interface est en francais alors
 * que le document se declarait `lang="en"`, ce qui trompe lecteurs d'ecran,
 * correcteurs orthographiques et cesures automatiques du navigateur.
 * `AppIntlLoader` ne posait ces attributs QUE pour les langues RTL.
 */
function useDocumentLanguage() {
  const { currentLocale, direction } = useAppIntlContext() || ({} as any);

  useEffect(() => {
    const html = document.documentElement;
    if (!html) return;

    if (currentLocale) html.setAttribute('lang', currentLocale);
    html.setAttribute('dir', direction || 'ltr');
  }, [currentLocale, direction]);
}

/**
 * App inner.
 */
function AppInsider({ history }: { history: History }) {
  // `AppInsider` n'est monte qu'une fois `intl.init()` resolu (AppIntlLoader
  // n'affiche ses enfants qu'apres chargement) : la langue est donc connue.
  useDocumentLanguage();

  return (
    <div className="App">
      <DashboardThemeProvider>
        <DialogProvider>
          {/* Root-level safety net: without this, a crash in ANY top-level
              route (/auth, the missing-docs/GED public portals, or a lazy
              chunk failing to load) unmounts the whole <Switch> and leaves a
              blank screen — react-router has no built-in error handling.
              DashboardPrivatePages already has its own nested boundary
              (DashboardContent.tsx), this one is the catch-all behind it. */}
          <ErrorBoundary FallbackComponent={DashboardErrorBoundary}>
            <Suspense fallback={<BrandedLoading />}>
              <Router history={history}>
                <Switch>
                  <Route
                    path={'/one_click_demo'}
                    children={<OneClickDemoPage />}
                  />
                  <Route path={'/auth/register/verify'}>
                    <EnsureAuthenticated>
                      <EnsureUserEmailNotVerified>
                        <RegisterVerify />
                      </EnsureUserEmailNotVerified>
                    </EnsureAuthenticated>
                  </Route>

                  <Route
                    path={'/auth/email_confirmation'}
                    children={<EmailConfirmation />}
                  />
                  <Route path={'/auth'} children={<AuthenticationPage />} />
                  <Route
                    path={'/payment/:linkId'}
                    children={<PaymentPortalPage />}
                  />
                  <Route
                    path={'/portal/missing-docs/:token'}
                    children={<MissingDocsPortalPage />}
                  />
                  <Route
                    path={'/portal/ged-request/:token'}
                    children={<GEDPortalPage />}
                  />
                  <Route path={'/'} children={<DashboardPrivatePages />} />
                  <Route path={'*'} children={<NotFoundPage />} />
                </Switch>
              </Router>
            </Suspense>
          </ErrorBoundary>

          <GlobalErrors />
        </DialogProvider>
      </DashboardThemeProvider>
    </div>
  );
}

/**
 * Core application.
 */
export default function App() {
  // Browser history.
  const history = createBrowserHistory();

  // Query client.
  const queryClient = new QueryClient(queryConfig);

  return (
    <QueryClientProvider client={queryClient}>
      <SplashScreen />

      <AppIntlLoader>
        <AppInsider history={history} />
      </AppIntlLoader>

      {/* F74 — les devtools react-query etaient montes en production ET
          ouverts par defaut : le panneau couvrait la moitie de l'ecran de
          connexion et son bouton flottant recouvrait le bas de la barre
          laterale (« SYSTEME » tronque en « EME », « Guide » et
          « Preferences » masques). Strictement reserve au mode developpement,
          et ferme au demarrage. */}
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
    </QueryClientProvider>
  );
}
